import { useCallback, useRef, useState } from 'react';
import axios from 'axios';

const usableSession = value => value && typeof value === 'object' && !Array.isArray(value)
  && typeof value.id === 'string' && value.id.length > 0;

// An older product-page response must not replace a later sync checkpoint/error.
export const latestSyncSession = (current, incoming) => {
  if (!usableSession(incoming)) return current || null;
  if (!usableSession(current)) return incoming;
  if (current.id === incoming.id) {
    if (current.status === 'cancelled') return current;
    if (incoming.status === 'cancelled') return incoming;
  }
  if (current.previousCancelledSessionId === incoming.id) return current;
  if (incoming.previousCancelledSessionId === current.id) return incoming;
  const currentTime = Date.parse(current.updatedAt || current.startedAt) || 0;
  const incomingTime = Date.parse(incoming.updatedAt || incoming.startedAt) || 0;
  const sameTimeAdvance = current.id === incoming.id && incomingTime === currentTime
    && (Number(incoming.processedCount || 0) > Number(current.processedCount || 0)
      || (incoming.status === 'complete' && current.status !== 'complete')
      || (incoming.status === 'partial' && current.status === 'running')
      || (incoming.lastError && !current.lastError));
  return incomingTime > currentTime || sameTimeAdvance ? incoming : current;
};

export const syncFailureMessage = (session, status) =>
  status === 401 || /\bHTTP\s+401\b/i.test(typeof session?.lastError === 'string' ? session.lastError : '')
    ? 'The receiving service rejected authentication. Check the connection before retrying.'
    : 'The sync request stopped. Check the connection and sync status before retrying.';

export const showcaseSyncDisplay = ({ active, startingNew, session, error, completionConfirmed }) => {
  if (active && startingNew) return { state: 'active', label: 'Starting new sync', message: 'A new publishing attempt has been requested.' };
  if (session?.status === 'cancelled') return { state: 'cancelled', label: 'Sync cancelled', message: `${error ? `${error} ` : ''}A remote request already in progress may still finish. Cancellation does not roll back remote writes.` };
  if (active) return { state: 'active', label: 'Sync in progress', message: 'A sync request is active in this browser.' };
  if (error && !completionConfirmed) return { state: 'paused', label: 'Sync paused', message: error };
  if (session?.status === 'complete') return { state: 'complete', label: 'Sync complete', message: error || '' };
  if (session?.status === 'partial') return { state: 'partial', label: 'Sync completed with errors', message: error || 'Some products could not be synced. Review the connection and results before retrying.' };
  if (error || (session?.lastError && session?.status === 'running')) {
    return { state: 'paused', label: 'Sync paused', message: error || syncFailureMessage(session) };
  }
  if (session?.status === 'running') return { state: 'unknown', label: 'Sync activity unknown', message: 'A saved session exists. Its current activity cannot be verified from this browser.' };
  return null;
};

export default function useShowcaseProductSync({ server, instanceUniqueId, headers, session, onSession, readSyncSession }) {
  const [observation, setObservation] = useState(null);
  const scope = useRef(instanceUniqueId);
  scope.current = instanceUniqueId;
  const inFlight = useRef(false);
  const cancelling = useRef(false);
  const [cancelPending, setCancelPending] = useState(false);
  const invalidated = useRef({ scope: instanceUniqueId, ids: new Set(), revision: 0 });
  if (invalidated.current.scope !== instanceUniqueId) invalidated.current = { scope: instanceUniqueId, ids: new Set(), revision: 0 };
  const isInvalidated = useCallback(value => invalidated.current.scope === instanceUniqueId && invalidated.current.ids.has(value?.id), [instanceUniqueId]);
  const local = observation?.scope === instanceUniqueId ? observation : null;
  const effectiveSession = latestSyncSession(isInvalidated(local?.session) ? null : local?.session, isInvalidated(session) ? null : session);
  const currentSession = useRef(effectiveSession);
  currentSession.current = effectiveSession;

  // Only an acknowledged membership write may obsolete this exact completed attempt.
  // Keep its ID blocked so a late page/status response cannot restore completion.
  const invalidateMembership = useCallback(expectedSessionId => {
    const saved = currentSession.current;
    if (scope.current !== instanceUniqueId || saved?.id !== expectedSessionId || !['complete', 'partial'].includes(saved?.status)) return;
    invalidated.current.ids.add(saved.id);
    invalidated.current.revision += 1;
    currentSession.current = null;
    setObservation({ scope: instanceUniqueId, session: null, active: false, error: '', completionConfirmed: false });
    onSession(null);
  }, [instanceUniqueId, onSession]);

  const execute = useCallback(async (expectedCancelledSessionId = null) => {
    if (inFlight.current) throw new Error('A sync request is already active in this browser.');
    inFlight.current = true;
    const revision = invalidated.current.revision;
    const isCurrent = () => scope.current === instanceUniqueId && invalidated.current.revision === revision;
    let lastSession = currentSession.current;
    let newStartPending = !!expectedCancelledSessionId;
    const observe = (patch) => {
      if (isCurrent()) setObservation(previous => ({ ...(previous?.scope === instanceUniqueId ? previous : {}), scope: instanceUniqueId, ...patch }));
    };
    const accept = incoming => {
      if (!isCurrent() || isInvalidated(incoming)) return;
      if (newStartPending) {
        if (!usableSession(incoming) || incoming.id === expectedCancelledSessionId || incoming.previousCancelledSessionId !== expectedCancelledSessionId) return;
        lastSession = latestSyncSession(currentSession.current, incoming);
        newStartPending = false;
        observe({ startingNew: false });
      } else lastSession = latestSyncSession(latestSyncSession(lastSession, currentSession.current), incoming);
      if (isCurrent()) {
        currentSession.current = lastSession;
        onSession(lastSession);
        observe({ session: lastSession });
      }
    };
    observe({ active: true, startingNew: newStartPending, error: '', refreshFailed: false, refreshing: false, completionConfirmed: false, session: lastSession });
    try {
      if (expectedCancelledSessionId && (lastSession?.id !== expectedCancelledSessionId || lastSession?.status !== 'cancelled')) throw new Error('The cancelled attempt changed. Refresh sync status.');
      let sessionId = lastSession?.status === 'running' ? lastSession.id : null;
      let completed = false;
      let finalData = null;
      for (let batch = 0; batch < 1000 && !completed; batch += 1) {
        if (!isCurrent()) throw new Error('Website products changed. Refresh sync status.');
        lastSession = latestSyncSession(lastSession, currentSession.current);
        if (lastSession?.status === 'cancelled' && !newStartPending) throw new Error('Sync cancelled. Remote writes are not rolled back.');
        const response = await axios.post(`${server}/promoshowcase/instances/${instanceUniqueId}/products/sync`,
          newStartPending ? { command: 'start-new', expectedCancelledSessionId, batchSize: 100 } : { sessionId, batchSize: 100 }, headers);
        if (!isCurrent()) throw new Error('Website products changed. Refresh sync status.');
        const data = response?.data || {};
        if (usableSession(data.session)) accept(data.session);
        if (newStartPending) throw new Error('The new sync attempt was not confirmed.');
        lastSession = latestSyncSession(lastSession, currentSession.current);
        if (lastSession?.status === 'cancelled') throw new Error('Sync cancelled. Remote writes are not rolled back.');
        sessionId = data.session?.id || sessionId;
        completed = !!data.complete;
        finalData = data;
        if (!completed && !sessionId) throw new Error('Missing sync session.');
      }
      if (!completed) throw new Error('Sync continuation limit reached.');
      observe({ error: '' });
      return finalData;
    } catch (failure) {
      if (!isCurrent()) throw new Error('Website products changed. Refresh sync status.');
      lastSession = latestSyncSession(lastSession, currentSession.current);
      const returned = failure?.response?.data?.session;
      if (usableSession(returned)) accept(returned);
      if (lastSession?.status === 'cancelled' && !newStartPending) throw new Error('Sync cancelled. Remote writes are not rolled back.');
      // Only fixed, user-facing text is exposed. Axios errors can contain auth headers.
      const message = newStartPending ? 'The new sync could not be started. Refresh sync status before trying again.' : syncFailureMessage(lastSession, failure?.response?.status);
      observe({ active: false, startingNew: false, error: message, refreshing: true });
      try {
        const beforeRefresh = lastSession;
        const authoritative = await readSyncSession(); // Exactly one GET; never a retry POST.
        if (usableSession(authoritative)) accept(authoritative);
        if (lastSession !== beforeRefresh && ['complete', 'partial', 'cancelled'].includes(lastSession?.status)) observe({ completionConfirmed: true });
      } catch (_refreshFailure) {
        observe({ refreshFailed: true });
      }
      throw new Error(message);
    } finally {
      inFlight.current = false;
      observe({ active: false, startingNew: false, refreshing: false });
    }
  }, [server, instanceUniqueId, headers, onSession, readSyncSession, isInvalidated]);

  const run = useCallback(() => execute(), [execute]);
  const startNew = useCallback(() => {
    if (inFlight.current) return Promise.reject(new Error('A sync request is already active in this browser.'));
    const saved = currentSession.current;
    if (saved?.status !== 'cancelled') return Promise.reject(new Error('Refresh sync status before starting a new attempt.'));
    if (!window.confirm('Start a new sync and publish the current website products to WordPress? The cancelled attempt stays cancelled in history. A previously sent remote request may still finish.')) return Promise.resolve(null);
    return execute(saved.id);
  }, [execute]);

  const cancel = useCallback(async () => {
    const saved = currentSession.current;
    if (!usableSession(saved) || cancelling.current) return;
    const targetScope = instanceUniqueId;
    cancelling.current = true; setCancelPending(true);
    try {
      const response = await axios.post(`${server}/promoshowcase/instances/${instanceUniqueId}/products/sync/cancel`, { sessionId: saved.id }, headers);
      const incoming = response?.data?.session;
      if (scope.current !== targetScope) return;
      if (!usableSession(incoming) || incoming.id !== saved.id || incoming.status !== 'cancelled') throw new Error('Invalid cancellation response.');
      const session = latestSyncSession(currentSession.current, incoming);
      currentSession.current = session; onSession(session);
      setObservation({ scope: targetScope, session, active: false, error: '', completionConfirmed: true });
      // Confirm through the read-only status request. A failed refresh cannot undo acknowledged cancellation.
      try { const confirmed = await readSyncSession(); if (scope.current === targetScope && confirmed?.id === saved.id) { const latest = latestSyncSession(currentSession.current, confirmed); currentSession.current = latest; onSession(latest); } }
      catch (_) { if (scope.current === targetScope) setObservation(previous => ({ ...previous, refreshFailed: true })); }
    } catch (_) {
      if (scope.current === targetScope) setObservation(previous => ({ ...(previous?.scope === targetScope ? previous : {}), scope: targetScope, error: 'Cancellation could not be confirmed. Refresh sync status before trying again.' }));
    } finally { cancelling.current = false; setCancelPending(false); }
  }, [server, instanceUniqueId, headers, onSession, readSyncSession]);

  const active = !!local?.active && (effectiveSession?.status !== 'cancelled' || !!local?.startingNew);
  const actionLabel = active ? 'Syncing...' : local?.refreshing ? 'Refreshing status...' : effectiveSession?.status === 'cancelled' ? 'Start new sync' : effectiveSession?.status === 'running' ? 'Resume sync' : 'Push to WordPress';
  return { run, startNew, cancel, invalidateMembership, cancelPending, active, actionLabel, startingNew: !!local?.startingNew, session: effectiveSession, error: local?.error || '',
    refreshing: !!local?.refreshing, refreshFailed: !!local?.refreshFailed, completionConfirmed: !!local?.completionConfirmed };
}
